/**
 * Kanban-style board that groups clients by health status (or other grouping).
 * Three columns: Healthy, Watch, At Risk — matching the design spec.
 */
import type { Client } from '../../lib/api'
import { ClientCard } from './ClientCard'
import { getHealthConfig } from './HealthBadge'

interface ClientBoardProps {
  clients: Client[]
  groupBy: 'health' | 'vertical' | 'plan' | 'account_manager'
  /** clientId → account-manager display name (for account_manager grouping). */
  amByClient?: Map<string, string>
}

interface ColumnDef {
  key: string
  label: string
  dot: string
  clients: Client[]
}

export function ClientBoard({ clients, groupBy, amByClient }: ClientBoardProps) {
  const columns = buildColumns(clients, groupBy, amByClient)

  return (
    <div className="client-board">
      {columns.map((col) => (
        <div key={col.key} className="client-board__col">
          <div className="client-board__col-header">
            <span className="client-board__col-dot" style={{ background: col.dot }} />
            <span className="client-board__col-title">{col.label}</span>
            <span className="client-board__col-count">{col.clients.length}</span>
          </div>
          <div className="client-board__col-body">
            {col.clients.length === 0 ? (
              <div className="client-board__empty">No clients</div>
            ) : (
              col.clients.map((c) => <ClientCard key={c.id} client={c} variant="board" />)
            )}
          </div>
        </div>
      ))}
    </div>
  )
}

function buildColumns(clients: Client[], groupBy: string, amByClient?: Map<string, string>): ColumnDef[] {
  if (groupBy === 'health') {
    return [
      {
        key: 'good',
        label: 'Healthy',
        dot: getHealthConfig('good').color,
        clients: clients.filter((c) => c.health === 'good'),
      },
      {
        key: 'watch',
        label: 'Watch',
        dot: getHealthConfig('watch').color,
        clients: clients.filter((c) => c.health === 'watch'),
      },
      {
        key: 'at-risk',
        label: 'At Risk',
        dot: getHealthConfig('at-risk').color,
        clients: clients.filter((c) => c.health === 'at-risk'),
      },
    ]
  }

  // Group by account manager (from the assignments map), bucketing unstaffed
  // clients under "Unassigned".
  const buckets = new Map<string, Client[]>()
  if (groupBy === 'account_manager') {
    for (const c of clients) {
      const key = amByClient?.get(c.id) || 'Unassigned'
      if (!buckets.has(key)) buckets.set(key, [])
      buckets.get(key)!.push(c)
    }
  } else {
    // Generic grouping by a string field (vertical, plan)
    const field = groupBy as keyof Client
    for (const c of clients) {
      const key = (c[field] as string) || 'Other'
      if (!buckets.has(key)) buckets.set(key, [])
      buckets.get(key)!.push(c)
    }
  }

  const colors = ['var(--accent-primary)', 'var(--state-warning)', 'var(--state-success)', 'var(--state-danger)']
  let i = 0
  return Array.from(buckets.entries()).map(([key, list]) => ({
    key,
    label: key,
    dot: colors[i++ % colors.length] ?? 'var(--accent-primary)',
    clients: list,
  }))
}
